/**
 * №11.2 — data hooks for the Firm-settings Roles & access panel. Reads/mutations
 * go through the same mcpClient transport; the admin verbs (assign_role,
 * create_group) are owner-gated at the worker.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

async function read<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

export interface AreaDecl { key: string; label: string; description?: string; money?: boolean; default_member?: boolean }
export interface RoleRow { name: string; areas: string[]; description: string | null; seeded: boolean }
export interface AssignmentRow { subject_kind: string; subject_id: string; role_name: string }
export interface GroupRow { id: string; name: string; members: string[] }

export const useAreas = () =>
  useQuery({ queryKey: ["policy-areas"], staleTime: 60_000, queryFn: () => read<{ areas: AreaDecl[]; default_member: string[] }>("list_areas") });
export const useRoles = () =>
  useQuery({ queryKey: ["policy-roles"], staleTime: 30_000, queryFn: async () => (await read<{ roles: RoleRow[] }>("list_roles")).roles ?? [] });
export const useRoleAssignments = () =>
  useQuery({ queryKey: ["policy-assignments"], staleTime: 30_000, queryFn: async () => (await read<{ assignments: AssignmentRow[] }>("list_role_assignments")).assignments ?? [] });
export const useGroups = () =>
  useQuery({ queryKey: ["policy-groups"], staleTime: 30_000, queryFn: async () => (await read<{ groups: GroupRow[] }>("list_groups")).groups ?? [] });

export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { subject_kind: string; subject_id: string; role_name: string }) => read("assign_role", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["policy-assignments"] }),
  });
}
export function useUnassignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { subject_kind: string; subject_id: string; role_name: string }) => read("unassign_role", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["policy-assignments"] }),
  });
}
export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; members: string[] }) => read("create_group", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["policy-groups"] }),
  });
}
export function useSetGroupMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { group_id: string; members: string[] }) => read("set_group_members", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["policy-groups"] }),
  });
}

// №11.2.4 — matter restriction (D-W2: lives on the matter record, not Firm settings).
export interface MatterRestriction { access: "workspace" | "restricted"; allowed: string[] }

export const useMatterRestriction = (matterId: string | undefined) =>
  useQuery({
    queryKey: ["matter-restriction", matterId],
    enabled: !!matterId,
    staleTime: 15_000,
    queryFn: () => read<{ matter_id: string; restriction: MatterRestriction }>("get_matter_restriction", { matter_id: matterId }),
  });

export function useRestrictMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { matter_id: string; access: "workspace" | "restricted"; allowed?: string[] }) =>
      read("restrict_matter", v),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ["matter-restriction", v.matter_id] }),
  });
}
